/**
 * DeleteAccountDialog — confirm and delete a RudderStack account through
 * `settings.rudderstackAccounts.delete` (spec §7, DV3). A `Dialog` per the UI skill's rule 12 —
 * never `window.confirm`.
 *
 * The platform refuses to delete an account still attached to a source, destination, or data graph
 * with an HTTP 409 naming the attached objects BY ID, not display name (confirmed live against a real
 * attached destination). Task 5 parses that into `RsAccountInUseError.usedBy` and the rpc layer maps
 * it to an ORPCError CONFLICT carrying `data.usedBy`. On that conflict this dialog stays open and
 * lists the groups, resolving destination ids to their RudderStack name where the workspace's
 * destinations list (`activations.listDestinations`) has one — confirmed live: two ids from a real
 * refusal came back as "Braze production" and "Braze staging" (design follow-up 2). Sources and data
 * graphs do not resolve through that list and stay ids.
 *
 * **Never blocks the refusal on the lookup.** The destinations read runs alongside the delete
 * mutation's own error handling, not before it, and a failed or slow read simply leaves every
 * destination id unresolved — the conflict itself always renders. Names are an enhancement to an
 * error that has to appear either way.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/_shared/components/ui/dialog';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { useRudderstackSignInAgain } from '@/_shared/hooks/useRudderstackSignInAgain';
import { orpcErrorMessage } from '@/_shared/lib/orpc-errors';

/** The platform's in-use groups, always ids — never display names (spec §7, DV3). */
interface UsedByGroups {
  sources: string[];
  destinations: string[];
  dataGraphs: string[];
}

/** The parsed groups, plus the platform's own (capped) reason text — optional enrichment alongside the
 *  groups, not a replacement for them (Vamsi L3 / Leo #1): `usedBy` can come back with every group
 *  empty (an upstream wording change, or the last truncated group `parseUsedBy` now drops rather than
 *  mangling), and `message` is what lets the dialog still say something real instead of an empty list. */
interface ConflictDetails {
  usedBy: UsedByGroups;
  message?: string;
  /** `usedBy` is only the part of the blocker list that survived the platform's 500-char cap. */
  truncated: boolean;
}

/** Duck-typed, like `orpc-errors.ts`'s `isOrpcConflictError` — the client error isn't always a real
 *  `ORPCError` instance (cross-realm instanceof isn't guaranteed), so read the JSON shape directly. */
function conflictDetailsFrom(err: unknown): ConflictDetails | null {
  if (!err || typeof err !== 'object') return null;
  const e = err as {
    code?: unknown;
    data?: { code?: unknown; usedBy?: unknown; message?: unknown; truncated?: unknown };
  };
  const code = e.code ?? e.data?.code;
  if (code !== 'CONFLICT') return null;
  const usedBy = e.data?.usedBy;
  if (!usedBy || typeof usedBy !== 'object') return null;
  const toIds = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((id): id is string => typeof id === 'string') : [];
  const u = usedBy as Record<string, unknown>;
  return {
    usedBy: {
      sources: toIds(u['sources']),
      destinations: toIds(u['destinations']),
      dataGraphs: toIds(u['dataGraphs']),
    },
    message: typeof e.data?.message === 'string' ? e.data.message : undefined,
    truncated: e.data?.truncated === true,
  };
}

const GROUP_LABELS: { key: keyof UsedByGroups; label: string }[] = [
  { key: 'destinations', label: 'Destinations' },
  { key: 'sources', label: 'Sources' },
  { key: 'dataGraphs', label: 'Data graphs' },
];

/**
 * Only `destinations` ever resolves to a name — `destinationNames` is the workspace's destinations
 * list keyed by id (`activations.listDestinations`, best-effort). Sources and data graphs don't
 * resolve through that list, so they always render their raw id (design follow-up 2).
 */
function usedByCaveat(usedBy: UsedByGroups, hasResolvedDestinationName: boolean): string {
  const idOnlyGroups = usedBy.sources.length > 0 || usedBy.dataGraphs.length > 0;
  if (usedBy.destinations.length > 0 && hasResolvedDestinationName) {
    return idOnlyGroups
      ? 'Destinations are shown by their RudderStack name where known. Sources and data graphs are shown as RudderStack ids.'
      : 'Destinations are shown by their RudderStack name where known.';
  }
  return 'These are platform ids, not the display names shown here:';
}

function UsedByCallout({
  usedBy,
  message,
  truncated,
  destinationNames,
}: {
  usedBy: UsedByGroups;
  /** The platform's own reason text — enrichment shown when there is no group to list (see
   *  `ConflictDetails`'s doc comment). */
  message?: string;
  /** True when the groups below are only PART of the blocker list (see `ConflictDetails`). */
  truncated: boolean;
  /** Destination id → RudderStack name, best-effort (design follow-up 2). */
  destinationNames: Map<string, string>;
}): React.JSX.Element {
  const hasAnyGroup = GROUP_LABELS.some((g) => usedBy[g.key].length > 0);
  const hasResolvedDestinationName = usedBy.destinations.some((id) => destinationNames.has(id));
  return (
    <Callout variant="error">
      <p className="font-medium text-foreground">This account is still in use and can&apos;t be deleted</p>
      {hasAnyGroup ? (
        <>
          <p className="mt-1 text-xs">{usedByCaveat(usedBy, hasResolvedDestinationName)}</p>
          <div className="mt-2 flex flex-col gap-2">
            {GROUP_LABELS.filter((g) => usedBy[g.key].length > 0).map((g) => (
              <div key={g.key}>
                <p className="text-xs font-semibold tracking-wide uppercase">{g.label}</p>
                <ul className="list-disc pl-4">
                  {usedBy[g.key].map((id) => {
                    // Only `destinations` ever has a resolved name to show — see this function's own
                    // doc comment. Falls back to the raw id, styled the same as every other unresolved
                    // id.
                    const name = g.key === 'destinations' ? destinationNames.get(id) : undefined;
                    return (
                      <li key={id} className={name ? 'text-xs' : 'font-mono text-xs'}>
                        {name ?? id}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </>
      ) : (
        // No group to list — a wording change upstream, or the last (truncated) group parseUsedBy now
        // drops rather than mangling. Fall back to the platform's own reason text when there is one;
        // otherwise a fixed note. Never silently render an empty callout (Vamsi L3 / Leo #1).
        <p className="mt-1 text-xs">{message ?? 'Details are unavailable.'}</p>
      )}
      {truncated && (
        // Truncation always lands in the LAST group, so `hasAnyGroup` is usually still true and the
        // fallback above never fires: without this the user is shown a partial blocker list as if it
        // were complete, clears it, and hits the same refusal with nothing new to go on. The platform's
        // own capped text rides alongside so the ids that survived the cut are at least readable.
        <>
          <p className="mt-2 text-xs">
            RudderStack&apos;s reply was cut off, so this list is incomplete — other objects may still be using this
            account.
          </p>
          {hasAnyGroup && message && <p className="mt-1 text-xs break-all">{message}</p>}
        </>
      )}
    </Callout>
  );
}

interface DeleteAccountDialogProps {
  account: { id: string; name: string } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  surface: 'activation' | 'settings';
  onDeleted?: () => void;
}

export function DeleteAccountDialog({
  account,
  open,
  onOpenChange,
  surface,
  onDeleted,
}: DeleteAccountDialogProps): React.JSX.Element {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const [conflict, setConflict] = useState<ConflictDetails | null>(null);
  // ACT2-1124: an ended RudderStack sign-in offers "Sign in again" on the toast.
  const signIn = useRudderstackSignInAgain();

  // Fetched unconditionally, never gating the conflict UI — a slow or failed read just leaves every
  // destination id unresolved (see this file's own header comment).
  const destinationsQuery = useQuery(orpc.activations.listDestinations.queryOptions({ input: {} }));
  const destinationNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const destination of destinationsQuery.data?.destinations ?? []) map.set(destination.id, destination.name);
    return map;
  }, [destinationsQuery.data]);

  const deleteMutation = useMutation(
    orpc.settings.rudderstackAccounts.delete.mutationOptions({
      onSuccess: () => {
        toast.success(account ? `Deleted ${account.name}` : 'Account deleted');
        onDeleted?.();
        // All account-backed query keys, always (shared UI notes §11): settings, activation picker,
        // and audience detail bindings read the same accounts, so invalidating only the settings key
        // would leave a deleted account still pickable there.
        void queryClient.invalidateQueries({ queryKey: orpc.settings.rudderstackAccounts.key() });
        void queryClient.invalidateQueries({ queryKey: orpc.audience.listActivationAccounts.key() });
        void queryClient.invalidateQueries({ queryKey: orpc.audience.listRudderstackWarehouseAccounts.key() });
        setConflict(null);
        onOpenChange(false);
      },
      onError: (err) => {
        const details = conflictDetailsFrom(err);
        if (details) {
          // Stay open; the groups (or the fallback message) render inline instead of a generic toast.
          setConflict(details);
          return;
        }
        if (signIn.toastIfReauth(err)) return;
        toast.error(orpcErrorMessage(err, 'Failed to delete account'));
      },
    }),
  );

  const handleOpenChange = (next: boolean): void => {
    if (!next) {
      if (deleteMutation.isPending) return;
      setConflict(null);
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        onEscapeKeyDown={(event) => {
          if (deleteMutation.isPending) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (deleteMutation.isPending) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Delete account?</DialogTitle>
          <DialogDescription>
            {account ? `${account.name} will be permanently removed from this workspace.` : ''}
          </DialogDescription>
        </DialogHeader>

        {conflict && (
          <UsedByCallout
            usedBy={conflict.usedBy}
            message={conflict.message}
            truncated={conflict.truncated}
            destinationNames={destinationNames}
          />
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => handleOpenChange(false)}
            disabled={deleteMutation.isPending}
            label="Cancel"
          />
          <Button
            type="button"
            variant="primary-destructive"
            loading={deleteMutation.isPending}
            disabled={deleteMutation.isPending}
            onClick={() => account && deleteMutation.mutate({ accountId: account.id, surface })}
            label="Delete"
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
